import type { ReactNode } from "react";
import { SectionTitle, SectionTitleLink } from "./section-title";
import { CardList } from "./card-list";

/**
 * @deprecated Use `<SectionTitle variant="card" …>` directly. This is now a thin
 * back-compat wrapper so every list/card chrome row renders through the one
 * canonical titling component (`section-title.tsx`). `ListCard` (below) already
 * uses `SectionTitle` directly.
 */
export function SectionHeader({
  title,
  subtitle,
  count,
  action,
}: {
  title: ReactNode;
  /** Optional muted line below the title. */
  subtitle?: ReactNode;
  count?: number;
  action?: ReactNode;
}) {
  return (
    <SectionTitle
      variant="card"
      title={title}
      subtitle={subtitle}
      count={count}
      action={action}
    />
  );
}

export function EmptyRow({ children }: { children: ReactNode }) {
  return (
    <div className="px-[var(--content-gutter)] py-[var(--section-gap)] type-body text-muted-foreground">
      {children}
    </div>
  );
}

/**
 * Card + the canonical title chrome (`SectionTitle variant="card"`) + EmptyRow
 * around a list-style child (typically a `<DataTable wrapInCard={false}>` or
 * `<FeedList>`). Used by the shell home, project home, and per-product
 * dashboards. When `count` is 0 and `empty` is set, renders the empty state
 * instead of `children`.
 *
 * Pass `viewAllHref` (preferred) for the standard muted "View all" link, or a
 * custom `action` node. The Card + header + footer chrome delegate to the one
 * flush-card recipe ({@link CardList}, VERTICAL-RHYTHM-PRD §T3), so the
 * title/count/link/footer match every other titled surface in the app.
 */
export function ListCard({
  title,
  subtitle,
  count,
  action,
  viewAllHref,
  viewAllLabel,
  empty,
  children,
  footer,
  className,
}: {
  title: ReactNode;
  /** Optional muted line below the title. */
  subtitle?: ReactNode;
  count?: number;
  /** Custom right-aligned action node. For a "View all" link, use viewAllHref. */
  action?: ReactNode;
  /** Shortcut: render the canonical muted "View all" link to this href. */
  viewAllHref?: string;
  /** Label for the viewAllHref link. Default "View all". */
  viewAllLabel?: string;
  /** Empty body shown when `count === 0`. Pass a string or any node. */
  empty?: ReactNode;
  children: ReactNode;
  /** Optional trailer row below the list (e.g. a "Show all" button). */
  footer?: ReactNode;
  className?: string;
}) {
  const isEmpty = count === 0 && empty !== undefined;
  // Resolve the viewAllHref shortcut into the one canonical action link here
  // (CardList takes the SectionTitle-shaped `action` slot). An explicit
  // `action` still wins, matching SectionTitle's own precedence.
  const resolvedAction =
    action ??
    (viewAllHref != null ? (
      <SectionTitleLink href={viewAllHref}>{viewAllLabel}</SectionTitleLink>
    ) : undefined);
  return (
    <CardList
      title={title}
      subtitle={subtitle}
      count={count}
      action={resolvedAction}
      footer={footer && !isEmpty ? footer : undefined}
      className={className}
    >
      {isEmpty ? <EmptyRow>{empty}</EmptyRow> : children}
    </CardList>
  );
}
